import { apiEvents } from "@/api/event.api.ts";
import { EventPreviewModal } from "@/components/event/EventPreviewModal.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useWorkflowExecutions } from "@/pages/workflows/workflow-execution-hook.tsx";
import {
  ApiWorkflowExecutionBlockDetail,
  ApiWorkflowExecutionBlockStatus,
  ApiWorkflowExecutionMeeting,
  ApiWorkflowExecutionTreeBlock,
} from "@/types/workflows/workflow-execution.types.ts";
import { WorkflowBlockNode } from "@/types/workflows/workflow-tree-builder.ts";
import {
  ApiWorkflowBlockActionTypes,
  ApiWorkflowBlockDelayReference,
  ApiWorkflowBlockTypes,
} from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowBlockEdgeShort } from "@/types/workflows/workflow.types.ts";
import {
  Avatar,
  Box,
  Button,
  FontAwesomeIcon,
  ListItem,
  Stack,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface ExecutionBlockNodeProps {
  node: WorkflowBlockNode<ApiWorkflowExecutionTreeBlock, ApiWorkflowBlockEdgeShort>;
  executionBlocksMap: Map<number, ApiWorkflowExecutionBlockDetail>;
  meetingsMap: Map<number, ApiWorkflowExecutionMeeting>;
  isLast: boolean;
}

export const ExecutionBlockNode = ({
  node,
  executionBlocksMap,
  meetingsMap,
  isLast,
}: ExecutionBlockNodeProps) => {
  const { t } = useTranslation();
  const { renderDateAndTime } = useFormatter();
  const { getWorkflowExecutionBlockStatusTag } = useWorkflowExecutions();
  const { openModal } = useModals();

  const [isEventLoading, setIsEventLoading] = useState(false);
  const [isEventUnavailable, setIsEventUnavailable] = useState(false);

  const { block } = node;
  const executionBlock = executionBlocksMap.get(block.id);

  const isMeetingCreated =
    block.action_type === ApiWorkflowBlockActionTypes.meeting &&
    executionBlock?.status === ApiWorkflowExecutionBlockStatus.completed;
  const meeting = isMeetingCreated ? meetingsMap.get(block.id) : undefined;

  const handleOpenMeeting = async (eventId: number) => {
    setIsEventLoading(true);
    try {
      const { data } = await apiEvents.getEventById(eventId);
      if (data) {
        openModal(EventPreviewModal, { event: data });
      } else {
        setIsEventUnavailable(true);
      }
    } finally {
      setIsEventLoading(false);
    }
  };

  const visualLevel = Math.max(0, node.level - 1);
  const children = node.children.filter((c) => c.block.block_type !== ApiWorkflowBlockTypes.end);

  const indentSize = 24;

  const getBlockIcon = (actionType: ApiWorkflowBlockActionTypes) => {
    switch (actionType) {
      case ApiWorkflowBlockActionTypes.send_notification:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-paper-plane"} />} />;
      case ApiWorkflowBlockActionTypes.http_request:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-globe"} />} />;
      case ApiWorkflowBlockActionTypes.google_workspace:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-brands fa-google"} />} />;
      case ApiWorkflowBlockActionTypes.invitation:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-envelope-open-text"} />} />;
      case ApiWorkflowBlockActionTypes.survey:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-square-poll-horizontal"} />} />;
      case ApiWorkflowBlockActionTypes.task:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-check"} />} />;
      case ApiWorkflowBlockActionTypes.performance_review:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-sharp fa-light fa-comment-pen"} />} />;
      case ApiWorkflowBlockActionTypes.performance_review_enroll:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-user-plus"} />} />;
      case ApiWorkflowBlockActionTypes.survey_enroll:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-user-plus"} />} />;
      case ApiWorkflowBlockActionTypes.run_workflow:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-diagram-project"} />} />;
      case ApiWorkflowBlockActionTypes.survey_with_approval:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-check-double"} />} />;
      case ApiWorkflowBlockActionTypes.esign:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-signature"} />} />;
      case ApiWorkflowBlockActionTypes.meeting:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-calendar"} />} />;
      case ApiWorkflowBlockActionTypes.candidate_email:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-envelope"} />} />;
      case ApiWorkflowBlockActionTypes.candidate_stage_change:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-arrow-right-arrow-left"} />} />;
      default:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-circle-dot"} />} />;
    }
  };

  const renderExecutionDates = (executionBlock: ApiWorkflowExecutionBlockDetail | undefined) => {
    if (!executionBlock) return undefined;

    const renderDateLine = (label: string, date: Date) => (
      <Text className={"text-secondary"}>
        {label}: {renderDateAndTime(date)}
      </Text>
    );

    if (
      executionBlock.delay_reference === ApiWorkflowBlockDelayReference.trigger_date &&
      executionBlock.datetime_effective_due_at
    ) {
      return (
        <Stack gap="xs">
          {renderDateLine(
            t("workflow:execution_block_effective_due_at"),
            executionBlock.datetime_effective_due_at
          )}
          <Text className="text-secondary text-sm">
            {t("workflow:execution_block_effective_due_at_hint")}
          </Text>
        </Stack>
      );
    }

    if (
      executionBlock.status === ApiWorkflowExecutionBlockStatus.scheduled &&
      executionBlock.datetime_scheduled_at
    ) {
      return renderDateLine(
        t("workflow:execution_block_scheduled_at"),
        executionBlock.datetime_scheduled_at
      );
    }

    if (!executionBlock.datetime_started_at) return undefined;

    if (executionBlock.datetime_completed_at) {
      return (
        <Stack gap={"xs"}>
          {renderDateLine(
            t("workflow:execution_block_started_at"),
            executionBlock.datetime_started_at
          )}
          {renderDateLine(
            t("workflow:execution_block_completed_at"),
            executionBlock.datetime_completed_at
          )}
        </Stack>
      );
    }

    return renderDateLine(
      t("workflow:execution_block_started_at"),
      executionBlock.datetime_started_at
    );
  };

  const getBlockPreTitle = (block: ApiWorkflowExecutionTreeBlock) => {
    if (block.title) return block.title;
    switch (block.action_type) {
      case ApiWorkflowBlockActionTypes.send_notification:
        return t("workflow:action_type_notification");
      case ApiWorkflowBlockActionTypes.http_request:
        return t("workflow:action_type_http_request");
      case ApiWorkflowBlockActionTypes.google_workspace:
        return t("workflow:action_type_google_workspace");
      case ApiWorkflowBlockActionTypes.invitation:
        return t("workflow:action_type_invitation");
      case ApiWorkflowBlockActionTypes.survey:
        return t("workflow:action_type_survey");
      case ApiWorkflowBlockActionTypes.task:
        return t("workflow:action_type_task");
      case ApiWorkflowBlockActionTypes.performance_review:
        return t("performance_review:create_performance_review");
      case ApiWorkflowBlockActionTypes.performance_review_enroll:
        return t("workflow:action_type_performance_review_add_employee");
      case ApiWorkflowBlockActionTypes.survey_enroll:
        return t("workflow:action_type_survey_enroll");
      case ApiWorkflowBlockActionTypes.run_workflow:
        return t("workflow:action_type_run_workflow");
      case ApiWorkflowBlockActionTypes.survey_with_approval:
        return t("workflow:action_type_approval");
      case ApiWorkflowBlockActionTypes.esign:
        return t("workflow:action_type_esign");
      case ApiWorkflowBlockActionTypes.meeting:
        return t("workflow:action_type_meeting");
      case ApiWorkflowBlockActionTypes.candidate_email:
        return t("workflow:action_type_candidate_email");
      case ApiWorkflowBlockActionTypes.candidate_stage_change:
        return t("workflow:action_type_candidate_stage_change");
      default:
        return undefined;
    }
  };

  const getBlockTitle = (block: ApiWorkflowExecutionTreeBlock) => block.subtitle || "-";

  const getCancelReasonText = (
    cancelReason: NonNullable<ApiWorkflowExecutionBlockDetail["cancel_reason"]>
  ) => {
    switch (cancelReason) {
      case "source_event_cancelled":
        return t("workflow:execution_block_cancel_reason_source_event_cancelled");
      case "block_deleted":
        return t("workflow:execution_block_cancel_reason_block_deleted");
      case "candidate_state_changed":
        return t("workflow:execution_block_cancel_reason_candidate_state_changed");
      case "esign_envelope_cancelled":
        return t("workflow:execution_block_cancel_reason_esign_envelope_cancelled");
    }
  };

  return (
    <Stack gap={"sm"}>
      {block.block_type === ApiWorkflowBlockTypes.action && (
        <div className={"relative"} style={{ marginLeft: `${visualLevel * indentSize}px` }}>
          {visualLevel > 0 && (
            <>
              <div
                className={"absolute w-px bg-dark/10"}
                style={{
                  left: `${-Math.floor(indentSize / 2)}px`,
                  top: "-8px",
                  height: isLast ? "calc(50% + 8px)" : "calc(100% + 8px)",
                }}
              />
              <div
                className={"absolute h-px bg-dark/10"}
                style={{
                  left: `${-Math.floor(indentSize / 2)}px`,
                  top: "50%",
                  width: `${Math.floor(indentSize / 2)}px`,
                }}
              />
            </>
          )}
          <Box variant={"whiteBorder"} className={"py-4"}>
            <ListItem
              icon={getBlockIcon(block.action_type)}
              behavior={"card"}
              emphasis={"top"}
              preTitle={getBlockPreTitle(block)}
              titleSlot={<Text className={"text-default"}>{getBlockTitle(block)}</Text>}
              captionSlot={renderExecutionDates(executionBlock)}
              valueSlot={
                executionBlock ? (
                  <Stack items={"end"} gap={"xs"} className={"shrink-0"}>
                    {getWorkflowExecutionBlockStatusTag(executionBlock.status)}
                  </Stack>
                ) : undefined
              }
            />
            {executionBlock?.status == ApiWorkflowExecutionBlockStatus.failed && (
              <Box className={"mt-2 mx-4 p-3 rounded-sm bg-(--color-danger-subtle)"}>
                <Text size={"sm"} color={"danger"}>
                  {executionBlock.error_message || t("workflow:execution_block_failed")}
                </Text>
              </Box>
            )}
            {executionBlock?.cancel_reason && (
              <Box className={"mt-2 mx-4 p-3 rounded-sm bg-dark/5"}>
                <Text size={"sm"} className={"text-secondary"}>
                  {getCancelReasonText(executionBlock.cancel_reason)}
                </Text>
              </Box>
            )}
            {isMeetingCreated && (
              <Box className={"mt-2 mx-4 p-3 rounded-sm bg-dark/5"}>
                <Stack gap={"xs"} items={"start"}>
                  <Text size={"sm"} className={"text-secondary"}>
                    {t("workflow:execution_block_meeting_created")}
                  </Text>
                  {meeting && !isEventUnavailable && (
                    <Button
                      variant={"secondary"}
                      size={"sm"}
                      isLoading={isEventLoading}
                      leftIcon={<FontAwesomeIcon icon={"fa-light fa-calendar"} />}
                      onClick={() => handleOpenMeeting(meeting.event_id)}
                    >
                      {t("workflow:execution_block_meeting_open")}
                    </Button>
                  )}
                  {isEventUnavailable && (
                    <Text size={"sm"} className={"text-secondary"}>
                      {t("workflow:execution_block_meeting_unavailable")}
                    </Text>
                  )}
                </Stack>
              </Box>
            )}
          </Box>
        </div>
      )}

      {children.map((child, index) => (
        <ExecutionBlockNode
          key={`${child.block.id}-${index}`}
          node={child}
          executionBlocksMap={executionBlocksMap}
          meetingsMap={meetingsMap}
          isLast={index === children.length - 1}
        />
      ))}
    </Stack>
  );
};
